import { PauseIcon, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { ComponentRenderer } from './ComponentRenderer'
import { PdfDownloadButton } from './PdfDownloadButton'
import type { EvaluateeData } from './types'
import { useAutoSave } from './useAutoSave'
import { useCycleExecution } from './useCycleExecution'

type FormValues = Record<string, string | number | boolean>

const EmployeeInfo = ({
	evaluateeData,
	evaluationHeaderConfig,
}: {
	evaluateeData: EvaluateeData
	evaluationHeaderConfig?: Array<{ id?: string; name: string; value: string; order: number }>
}) => {
	const employeeInfo = evaluationHeaderConfig
		? evaluationHeaderConfig
				.sort((a, b) => a.order - b.order)
				.map((field) => ({
					label: field.name,
					value: field.value || '-',
				}))
		: [
				{
					label: 'Employee Name',
					value: evaluateeData ? `${evaluateeData.firstName || ''} ${evaluateeData.lastName || ''}`.trim() : '-',
				},
				...(evaluateeData?.position ? [{ label: 'Position', value: evaluateeData?.position || '-' }] : []),
				...(evaluateeData?.directManager
					? [
							{
								label: 'Primary Supervisor',
								value:
									`${evaluateeData.directManager.firstName || ''} ${
										evaluateeData.directManager.lastName || ''
									}`.trim() || '-',
							},
						]
					: []),
			]

	return (
		<div className='flex flex-col gap-4 rounded-md bg-blue-50 border border-blue-200 scrollbar-visible m-3 p-3'>
			{employeeInfo.map((info) => (
				<div key={info.label} className='min-w-0'>
					<Text variant='hint' className='text-neutral-400 mb-1'>
						{info.label}
					</Text>
					<Text variant='xsmall' className='break-words'>
						{info.value}
					</Text>
				</div>
			))}
		</div>
	)
}

const Sidebar = ({
	evaluateeData,
	evaluationHeaderConfig,
	taskId,
}: {
	evaluateeData: EvaluateeData
	evaluationHeaderConfig?: Array<{ id?: string; name: string; value: string; order: number }>
	taskId?: string
}) => {
	return (
		<div className='sidebar no-print flex flex-col w-70 border-r border-neutral-200 overflow-y-auto pb-44'>
			<EmployeeInfo evaluateeData={evaluateeData} evaluationHeaderConfig={evaluationHeaderConfig} />

			{taskId && (
				<div className='flex flex-col px-4 py-4 border-b border-neutral-200'>
					<Text variant='xsmall' className='text-neutral-400 mb-3'>
						Actions
					</Text>
					<PdfDownloadButton taskId={taskId} variant='secondary' size='small' className='w-full' />
				</div>
			)}
		</div>
	)
}

const HoldStatusBanner = ({ isAutoSaving }: { isAutoSaving: boolean }) => {
	return (
		<div className='flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-md m-4'>
			<PauseIcon className='w-5 h-5 fill-amber-600 flex-shrink-0' />
			<div className='flex flex-col'>
				<Text variant='small' weight='semibold' className='text-amber-800'>
					{isAutoSaving ? 'Saving...' : 'Hold in Progress'}
				</Text>
				<Text variant='xsmall' className='text-amber-700'>
					Your changes are being auto-saved. The cycle admin will release the hold when ready.
				</Text>
			</div>
		</div>
	)
}

export const HoldStep = () => {
	const { taskId } = useParams<{ taskId: string; cycleId: string }>()
	const [isInitialized, setIsInitialized] = useState(false)

	const {
		cycle,
		cycleStep,
		formComponents,
		taskData,
		canEdit,
		isLoading,
		isError,
		refetch,
		calculatedRole,
		evaluationHeaderConfig,
	} = useCycleExecution({ taskId: taskId as string })

	const methods = useForm<FormValues>()
	const { control, watch, reset } = methods
	const watchedValues = watch()

	// Auto-save hook
	const { isAutoSaving } = useAutoSave({
		taskId: taskId || '',
		formData: watchedValues,
		isEnabled: !!taskId && canEdit && isInitialized,
	})

	// Initialize form with saved values from task data
	useEffect(() => {
		if (taskData && formComponents.length > 0 && !isInitialized) {
			// Build initial values from form components that have responses
			const initialValues: FormValues = {}
			const extractValues = (components: typeof formComponents) => {
				for (const comp of components) {
					if (comp.response !== undefined && comp.response !== null) {
						initialValues[comp.id] = comp.response as string | number | boolean
					}
					if (comp.children) {
						extractValues(comp.children)
					}
				}
			}
			extractValues(formComponents)
			reset(initialValues)
			setIsInitialized(true)
		}
	}, [taskData, formComponents, reset, isInitialized])

	if (isLoading) {
		return (
			<div className='flex items-center justify-center h-full'>
				<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600' />
			</div>
		)
	}

	if (isError || !taskData) {
		return (
			<div className='flex flex-col items-center justify-center h-full gap-4'>
				<Text variant='small' className='text-red-600'>
					Failed to load hold step data
				</Text>
				<button
					type='button'
					onClick={() => refetch()}
					className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'>
					Retry
				</button>
			</div>
		)
	}

	const evaluationId = taskData.evaluation?.id || ''
	const currentStepId = taskData.cycleStepId || ''
	const userRole = calculatedRole || 'participant'
	const participant = (taskData.participant ?? {}) as EvaluateeData

	return (
		<div className='flex flex-col h-full bg-white'>
			{/* Header */}
			<div className='border-b border-neutral-200 p-4'>
				<Text variant='hint' className='text-neutral-500'>
					{cycle?.name}
				</Text>
				<Text variant='small' weight='semibold'>
					{cycleStep?.name || 'Hold'}
				</Text>
			</div>

			<FormProvider {...methods}>
				<form className='flex flex-1 overflow-hidden'>
					{/* Sidebar with employee info and PDF button */}
					<Sidebar evaluateeData={participant} evaluationHeaderConfig={evaluationHeaderConfig} taskId={taskId} />

					{/* Main content area */}
					<div className='flex flex-col flex-1 overflow-y-auto'>
						<HoldStatusBanner isAutoSaving={isAutoSaving} />

						{/* Form components */}
						{formComponents.length > 0 ? (
							<div className='px-4 pb-6'>
								{formComponents.map((component) => (
									<ComponentRenderer
										key={component.id}
										component={component}
										control={control}
										evaluationId={evaluationId}
										currentStepId={currentStepId}
										userRole={userRole}
										canEdit={canEdit}
										taskId={taskId}
										refetch={refetch}
									/>
								))}
							</div>
						) : (
							<div className='flex items-center justify-center h-64'>
								<Text variant='small' className='text-gray-500'>
									No form content available during this hold step.
								</Text>
							</div>
						)}

						{/* Footer */}
						<div className='border-t border-neutral-200 p-4 bg-white mt-auto'>
							<Text variant='xsmall' className='text-neutral-500'>
								{isAutoSaving ? 'Saving changes...' : 'All changes are auto-saved'}
							</Text>
						</div>
					</div>
				</form>
			</FormProvider>
		</div>
	)
}
